import React, { useEffect, useState } from "react";
import { DataGrid, GridColDef, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { Add, Edit, Delete, Visibility } from "@mui/icons-material";
import data_calendar from "./../../../data/zodiac/zodiac_calendar.json";
import { ENDPOINTS, getApiUrl } from "../../../common/endpoints";
import { Requests } from "../../../common/requests";
import { useSnackbar } from "notistack";
import { day_month, IS_SHOW_IMPORT, ZodiacOptions } from "../../../constants";
import { data_grid_styles, text_field_styles } from "../../../constants/style";
import TogglePublicBtn from "../../../components/TogglePublicBtn";

// Initial data

const ZodiacCalendar: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [formData, setFormData] = useState<any>({
    day: "",
    zodiac: "",
    description: "",
  });
  const [isImporting, setIsImporting] = useState(false);
  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_CALENDAR_LIST + "?limit=0");
    try {
      const data = await Requests.get(apiUrl);
      setData(data.sort((a: any, b: any) => a.day - b.day));
    } catch (e) {}
  };

  const onTogglePublish = async (id: string) => {
    try {
      const apiUrl = getApiUrl(
        ENDPOINTS.ZODIAC_CALENDAR_TOGGLE_PUBLISH.replace(":id", id)
      );
      await Requests.patch(apiUrl);
      getData();
      enqueueSnackbar("Toggle publish success!", { variant: "success" });
    } catch (e) {}
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: "day",
      headerName: "Day",
      width: 100,
      renderCell: (data) => {
        return (
          <Box>
            {data.value} ({day_month[data.value - 1]})
          </Box>
        );
      },
    },
    { field: "zodiac", headerName: "Zodiac", width: 130 },
    { field: "description", headerName: "Description", width: 300, flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 270,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Delete"
          onClick={() => handleDelete(params.id.toString())}
        />,
        <GridActionsCellItem
          icon={<Visibility />}
          label="View"
          onClick={() => handleView(params.row)}
        />,
        <TogglePublicBtn
          isValidated={!!params.row?.ispublished}
          onToggle={() => {
            onTogglePublish(params.row?.id);
          }}
        />,
      ],
    },
  ];

  // Add new row
  const handleAdd = () => {
    setOpenDialog(true);
    setEditingRow(null);
    setFormData({ id: data.length + 1, day: "", zodiac: "", description: "" });
  };

  // Edit existing row
  const handleEdit = (row: any) => {
    setEditingRow(row);
    setFormData(row);
    setOpenDialog(true);
  };

  // View details
  const handleView = (row: any) => {
    setSelectedRow(row);
    setDetailsDialog(true);
  };

  // Save form data
  const handleSave = async () => {
    try {
      if (editingRow?.id) {
        setData(
          data.map((item) => (item.id === editingRow.id ? formData : item))
        );
        const apiUrl = getApiUrl(
          ENDPOINTS.ZODIAC_CALENDAR_EDIT.replace(":id", editingRow.id)
        );
        await Requests.put(apiUrl, formData);
      } else {
        const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_CALENDAR_ADD);
        await Requests.post(apiUrl, formData);
      }
      setOpenDialog(false);
      setFormData({});
      getData();
      enqueueSnackbar("Save successfully!", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Save unsuccessfully!", { variant: "error" });
    }
  };

  // Delete row
  const handleDelete = async (id: string) => {
    const apiUrl = getApiUrl(
      ENDPOINTS.ZODIAC_CALENDAR_DELETE.replace(":id", id)
    );
    await Requests.delete(apiUrl);
    getData();
  };

  // Handle input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  // Dialog close
  const handleClose = () => {
    setOpenDialog(false);
  };

  // Detail dialog close
  const handleDetailsClose = () => {
    setDetailsDialog(false);
    setSelectedRow(null);
  };
  const onImportData = async () => {
    setIsImporting(true);
    const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_CALENDAR_BULK);
    try {
      const nbReq = data_calendar.length / 10;
      for (let i = 0; i <= nbReq; i++) {
        await Requests.post(apiUrl, data_calendar.slice(i * 10, (i + 1) * 10));
      }
      getData();
      enqueueSnackbar("Import successfully!", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Import unsuccessfully!", { variant: "error" });
    }
    setIsImporting(false);
  };
  return (
    <Box m={2}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          color="primary"
          variant="h5"
          sx={{
            fontWeight: "bold",
          }}
          gutterBottom
        >
          Zodiac Calendar
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            sx={{
              textTransform: "unset",
              marginRight: "10px",
            }}
            startIcon={<Add />}
            onClick={handleAdd}
          >
            Add
          </Button>
          {IS_SHOW_IMPORT && (
            <Button
              variant="contained"
              color="primary"
              sx={{
                textTransform: "unset",
              }}
              onClick={() => {
                onImportData();
              }}
              disabled={isImporting}
            >
              {isImporting && (
                <CircularProgress
                  size="30px"
                  sx={{
                    marginRight: "10px",
                    color: "#fff",
                  }}
                />
              )}
              Import
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          height: "calc(100vh - 80px)",
          overflowY: "auto",
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          checkboxSelection={false}
          sx={{
            ...data_grid_styles,
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleClose}>
        <DialogTitle
          color="primary"
          sx={{
            fontWeight: "bold",
          }}
        >
          {editingRow ? "Edit Zodiac" : "Add Zodiac"}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="day"
            label="Day"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.day}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            select
            name="zodiac"
            label="Zodiac"
            fullWidth
            value={formData.zodiac}
            onChange={handleChange}
            required
            sx={{
              ...text_field_styles,
            }}
          >
            {ZodiacOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            name="inOneWord"
            label="inOneWord"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.inOneWord}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="shape"
            label="shape"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.shape}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="strength"
            label="strength"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.strength}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="weakness"
            label="weakness"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.weakness}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            select
            name="mostCompatible"
            label="Most compatible"
            fullWidth
            value={formData.mostCompatible}
            onChange={handleChange}
            required
            sx={{
              ...text_field_styles,
            }}
          >
            {ZodiacOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.description}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="horoscope"
            label="horoscope"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.horoscope}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="loveAndEmotions"
            label="loveAndEmotions"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.loveAndEmotions}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="purpose"
            label="purpose"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.purpose}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="positiveTraits"
            label="positiveTraits"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.positiveTraits}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="negativeTraits"
            label="negativeTraits"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.negativeTraits}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="healingCrystal"
            label="healingCrystal"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.healingCrystal}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="sabianSymbol"
            label="sabianSymbol"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.sabianSymbol}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="famousBirthdays"
            label="famousBirthdays"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.famousBirthdays}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="importantHistoricalEvents"
            label="importantHistoricalEvents"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.importantHistoricalEvents}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            sx={{ textTransform: "unset" }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: "unset" }}
            type="submit"
            onClick={() => handleSave()}
          >
            {editingRow ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedRow && (
        <Dialog open={detailsDialog} onClose={handleDetailsClose}>
          <DialogTitle>Zodiac Details</DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Day</strong>: {selectedRow.day}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Zodiac</strong>: {selectedRow.zodiac}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Description</strong>: {selectedRow.description}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Horoscope</strong>: {selectedRow.horoscope}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Love and Emotions</strong>: {selectedRow.loveAndEmotions}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Purpose</strong>: {selectedRow.purpose}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Positive Traits</strong>: {selectedRow.positiveTraits}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Negative Traits</strong>: {selectedRow.negativeTraits}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{
                textTransform: "unset",
              }}
              onClick={handleDetailsClose}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ZodiacCalendar;
